'use client';

import type { Dispatch, SetStateAction } from 'react';
import { CountryScopeControl } from '@/new_components/shared/CountryScopeControl';
import type { i18n as I18n, TFunction } from 'i18next';
import { Check, ChevronDown, MapPin, X } from 'lucide-react';
import { countryScopeOptions, swissCantonLabel } from '@/lib/countries';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { FilterGroup } from '../FilterGroup';
import { deFold } from '../searchSidebarOptions';

interface LocationFilterGroupProps {
  t: TFunction<'translation', undefined>;
  locationCount: number;
  clearLocation: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | 'pro' | null;
  lockOf: (control: string) => 'user' | 'pro' | null;
  cityOpen: boolean;
  setCityOpen: Dispatch<SetStateAction<boolean>>;
  setCitySearch: Dispatch<SetStateAction<string>>;
  selectedCities: string[];
  removeSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  setSelectedCities: Dispatch<SetStateAction<string[]>>;
  citySearch: string;
  cityList: string[];
  toggleSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  facetCount: (facet: string, value: string) => number | undefined;
  onCountryChange: ((country: string) => void) | undefined;
  country: string;
  bundeslandOpen: boolean;
  setBundeslandOpen: Dispatch<SetStateAction<boolean>>;
  selectedBundesland: string[];
  i18n: I18n;
  setSelectedBundesland: Dispatch<SetStateAction<string[]>>;
  locationOptions: { value: string; label: string }[];
}

export function LocationFilterGroup({
  t,
  locationCount,
  clearLocation,
  openFilterGroup,
  handleGroupOpenChange,
  groupLock,
  lockOf,
  cityOpen,
  setCityOpen,
  setCitySearch,
  selectedCities,
  removeSet,
  setSelectedCities,
  citySearch,
  cityList,
  toggleSet,
  facetCount,
  onCountryChange,
  country,
  bundeslandOpen,
  setBundeslandOpen,
  selectedBundesland,
  i18n,
  setSelectedBundesland,
  locationOptions,
}: LocationFilterGroupProps) {
  return (
    <FilterGroup
      id="location"
      icon={MapPin}
      label={t('search.sidebar.groups.location', { defaultValue: 'Location' })}
      activeCount={locationCount}
      onClear={clearLocation}
      open={openFilterGroup === 'location'}
      onOpenChange={handleGroupOpenChange('location')}
      lock={groupLock('location')}
    >
      {/* City */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('city')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.city')}
        </FilterLabel>
        <Popover
          open={lockOf('city') ? false : cityOpen}
          onOpenChange={(open) => {
            setCityOpen(open);
            if (!open) setCitySearch('');
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={!!lockOf('city')}
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {selectedCities.length === 0 ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.cityPlaceholder')}
                  </span>
                ) : (
                  selectedCities.map((c) => (
                    <Badge
                      key={c}
                      variant="secondary"
                      className="text-xs font-normal gap-1 pr-1 border border-foreground"
                    >
                      <span className="max-w-[220px] truncate">{c}</span>
                      <X
                        size={12}
                        className="cursor-pointer hover:bg-muted rounded-sm"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          removeSet(c, setSelectedCities);
                        }}
                      />
                    </Badge>
                  ))
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                className={INPUT_PLACEHOLDER_CLASS}
                placeholder={t('search.sidebar.searchCities', {
                  defaultValue: 'Search cities...',
                })}
                value={citySearch}
                onValueChange={setCitySearch}
              />
              <CommandList className="max-h-[272px]">
                <CommandEmpty>
                  {t('search.sidebar.noCityFound', {
                    defaultValue: 'No city found.',
                  })}
                </CommandEmpty>
                <CommandGroup>
                  {cityList
                    .filter(
                      (c) =>
                        !citySearch || deFold(c).includes(deFold(citySearch)),
                    )
                    .slice(0, 100)
                    .map((c) => (
                      <CommandItem
                        key={c}
                        value={c}
                        onSelect={() => toggleSet(c, setSelectedCities)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedCities.includes(c)
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <span className="truncate text-xs">{c}</span>
                        <FacetCount count={facetCount('city', c)} />
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Country, right above the Bundesland list it narrows. Applied
                at once (SearchPageClient's applyCountry), like the old header
                flag; every country is the default. */}
      {onCountryChange && (
        <CountryScopeControl
          value={country}
          options={['', ...countryScopeOptions('searchScope')]}
          onChange={onCountryChange}
        />
      )}

      {/* Bundesland */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('Bundesland')} className={FIELD_LABEL_CLASS}>
          {country === 'CH'
            ? t('search.sidebar.canton', { defaultValue: 'Canton' })
            : country === ''
              ? t('search.sidebar.stateOrCanton', {
                  defaultValue: 'State / canton',
                })
              : t('search.sidebar.bundesland', {
                  defaultValue: 'Federal state',
                })}
        </FilterLabel>
        <Popover
          open={lockOf('Bundesland') ? false : bundeslandOpen}
          onOpenChange={setBundeslandOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={!!lockOf('Bundesland')}
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {selectedBundesland.length === 0 ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.statePlaceholder', {
                      defaultValue: 'e.g. Bavaria',
                    })}
                  </span>
                ) : (
                  selectedBundesland.map((b) => (
                    <Badge
                      key={b}
                      variant="secondary"
                      className="text-xs font-normal gap-1 pr-1 border border-foreground"
                    >
                      <span className="max-w-[220px] truncate">
                        {swissCantonLabel(b, i18n.language)}
                      </span>
                      <X
                        size={12}
                        className="cursor-pointer hover:bg-muted rounded-sm"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          removeSet(b, setSelectedBundesland);
                        }}
                      />
                    </Badge>
                  ))
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandInput
                className={INPUT_PLACEHOLDER_CLASS}
                placeholder={t('search.sidebar.searchStates', {
                  defaultValue: 'Search states...',
                })}
              />
              <CommandList>
                <CommandEmpty>
                  {t('search.sidebar.noStateFound', {
                    defaultValue: 'No state found.',
                  })}
                </CommandEmpty>
                <CommandGroup>
                  {locationOptions.map(({ value: b, label }) => (
                    <CommandItem
                      key={b}
                      value={b}
                      keywords={[label]}
                      onSelect={() => toggleSet(b, setSelectedBundesland)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedBundesland.includes(b)
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className="truncate text-xs">{label}</span>
                      <FacetCount count={facetCount('bundesland', b)} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </FilterGroup>
  );
}
